import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Small, Divider } from "orcs-design-system";
import { isNil } from "lodash";
import GroupHeading from "src/components/Search/sub-components/GroupHeading";
import OptionBadgeWithActions from "src/components/OptionBadgeWithActions";
import OptionBadge from "src/components/OptionBadge";
import OptionTagBadge from "src/components/OptionTagBadge";
import ViewMoreResults from "./ViewMoreResults";

const getStableKey = (item, index) => {
  switch (item?.type) {
    case "person":
      return `person-${item?.value?.person?.aggregateId ?? index}`;
    case "team":
      return `team-${item?.value?.group?.id ?? index}`;
    case "tag":
      return `tag-${item?.value?.tag?.id ?? index}`;
    default:
      return `item-${index}`;
  }
};

const SearchResults = ({
  header,
  collection,
  selectedString,
  foundCount,
  totalCount,
  limitCount,
  viewMoreURL,
  workspace,
  onLoadMore,
  compactViewMore = false,
}) => {
  // counts are nil for loading, 0 for results, x for results
  const isLoading = isNil(foundCount) || isNil(totalCount);
  const foundNone = foundCount === 0;

  const Items = useMemo(() => {
    return collection
      .slice(0, limitCount ?? collection.length)
      .map((item, index) => {
        const key = getStableKey(item, index);
        const isPlaceholder = Boolean(item.isPlaceholder);

        switch (item.type) {
          case "person":
            return (
              <OptionBadgeWithActions
                key={key}
                isLoading={isPlaceholder}
                data={item.value}
                showPopover={true}
                selectedString={selectedString}
              />
            );
          case "team":
            return (
              <OptionBadge
                key={key}
                isLoading={isPlaceholder}
                data={item.value}
                workspace={workspace}
                groupTypes={workspace?.config?.groupTypes ?? []}
                selectedString={selectedString}
              />
            );
          case "tag":
            return (
              <OptionTagBadge
                key={key}
                isLoading={isPlaceholder}
                data={item.value}
                selectedString={selectedString}
              />
            );
          default:
            return null;
        }
      })
      .filter(Boolean);
  }, [collection, limitCount, selectedString, workspace]);

  const FooterDivider = useMemo(() => {
    // we don't have an item dividing line when there is only 1 for some reason
    if ((collection?.length ?? 0) === 1) {
      return (
        <Box px="s">
          <Divider light />
        </Box>
      );
    }
    return null;
  }, [collection?.length]);

  const Footer = useMemo(() => {
    if (isLoading) {
      return (
        <Box p="r" bg="transparent" style={{ textAlign: "center" }}>
          <Small display="block">Loading...</Small>
        </Box>
      );
    }

    if (foundCount && totalCount && viewMoreURL) {
      return (
        <ViewMoreResults
          foundCount={Math.min(foundCount, limitCount ?? foundCount)}
          totalCount={totalCount}
          viewMoreURL={viewMoreURL}
          onLoadMore={onLoadMore}
          compact={compactViewMore}
        />
      );
    }

    return null;
  }, [
    compactViewMore,
    foundCount,
    isLoading,
    limitCount,
    onLoadMore,
    totalCount,
    viewMoreURL,
  ]);

  if (foundNone) {
    return (
      <>
        <GroupHeading label={header} />
        <Box pt="r" pb="r" pl="r">
          No results found
        </Box>
      </>
    );
  }

  return (
    <>
      <GroupHeading label={header} />
      {Items}
      {FooterDivider}
      {Footer}
    </>
  );
};

SearchResults.propTypes = {
  header: PropTypes.string.isRequired,
  collection: PropTypes.array.isRequired,
  selectedString: PropTypes.string,
  foundCount: PropTypes.number,
  totalCount: PropTypes.number,
  limitCount: PropTypes.number,
  viewMoreURL: PropTypes.string,
  onLoadMore: PropTypes.func,
  workspace: PropTypes.object.isRequired,
  compactViewMore: PropTypes.bool,
};

export default SearchResults;
